import { getQueryParams, QueryParams } from "types/queryParams.types";
import { baseApi } from "./baseApi";
import { TransportOrder, TransportOrderContainer } from "types/command.types";
import { FormatResponse, TAG_TYPES } from "types";

const URL_TRANSPORT_ORDER = "transport-orders";

export const transportApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTransportOrders: builder.query<
      {
        statusCode: number;
        success: boolean;
        data: {
          kpis: {
            NHAP: number;
            XUAT: number;
            TAI_XUAT: number;
          };
          count: number;
          rows: TransportOrder[];
        };
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const normalizedQueryParams: Record<
          string,
          string | number | undefined
        > = Object.fromEntries(
          Object.entries(queryParams).map(([k, v]) => {
            if (typeof v === "boolean") return [k, v ? "true" : "false"];
            return [k, v ?? undefined];
          }),
        );
        const query = getQueryParams(normalizedQueryParams);
        if (query === "") return { url: URL_TRANSPORT_ORDER, method: "GET" };
        return {
          url: `${URL_TRANSPORT_ORDER}?${query}`,
          method: "GET",
        };
      },
      providesTags: (result) => {
        return result
          ? [
              ...(result?.data.rows || []).map(({ id }) => ({
                type: TAG_TYPES.TRANSPORT_ORDER,
                id,
              })),
              { type: TAG_TYPES.TRANSPORT_ORDER, id: "LIST" },
            ]
          : [{ type: TAG_TYPES.TRANSPORT_ORDER, id: "LIST" }];
      },
    }),
    getTransportOrderDetail: builder.query<
      { statusCode: number; success: boolean; data: TransportOrder },
      { id: string; query?: QueryParams }
    >({
      query: ({ id, query }) => {
        const queryString = query ? `?${getQueryParams(query)}` : "";

        return {
          url: `${URL_TRANSPORT_ORDER}/${id}${queryString}`,
          method: "GET",
        };
      },
      providesTags: (result, error, { id }) => [
        { type: TAG_TYPES.TRANSPORT_ORDER, id },
      ],
    }),
    createTransportOrder: builder.mutation<
      {
        statusCode: number;
        success: boolean;
        data: TransportOrder;
        message: string | string[];
      },
      TransportOrder
    >({
      query: (data) => ({
        url: URL_TRANSPORT_ORDER,
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.TRANSPORT_ORDER, id: "LIST" }],
    }),
    updateTransportOrder: builder.mutation<
      { statusCode: number; success: boolean; data: TransportOrder },
      { id: string } & Partial<TransportOrder>
    >({
      query: ({ id, ...data }) => ({
        url: `${URL_TRANSPORT_ORDER}/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: TAG_TYPES.TRANSPORT_ORDER, id },
        { type: TAG_TYPES.TRANSPORT_ORDER, id: "LIST" },
      ],
    }),
    createTransportOrderContainer: builder.mutation<
      FormatResponse<TransportOrderContainer>,
      {
        transportOrderId: string;
      } & Partial<TransportOrderContainer>
    >({
      query: ({ transportOrderId, ...data }) => ({
        url: `${URL_TRANSPORT_ORDER}/${transportOrderId}/containers`,
        method: "POST",
        data,
      }),
      invalidatesTags: (result, error, { transportOrderId }) => [
        { type: TAG_TYPES.TRANSPORT_ORDER, id: transportOrderId },
        { type: TAG_TYPES.TRANSPORT_ORDER, id: "LIST" },
      ],
    }),
    updateTransportOrderContainer: builder.mutation<
      FormatResponse<TransportOrderContainer>,
      {
        id: string;
        transportOrderId: string;
        tocDocuments?: { fileName: string; fileUrl: string }[];
      } & Partial<TransportOrderContainer>
    >({
      query: ({ id, transportOrderId, ...data }) => ({
        url: `${URL_TRANSPORT_ORDER}/${transportOrderId}/containers/${id}`,
        method: "PUT",
        data,
      }),
      invalidatesTags: (result, error, { transportOrderId }) => [
        { type: TAG_TYPES.TRANSPORT_ORDER, id: transportOrderId },
        { type: TAG_TYPES.TRANSPORT_ORDER, id: "LIST" },
      ],
    }),
    removeTransportOrderContainer: builder.mutation<
      FormatResponse<TransportOrderContainer>,
      {
        id: string;
        transportOrderId: string;
      }
    >({
      query: ({ id, transportOrderId }) => ({
        url: `${URL_TRANSPORT_ORDER}/${transportOrderId}/containers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { transportOrderId }) => [
        { type: TAG_TYPES.TRANSPORT_ORDER, id: transportOrderId },
        { type: TAG_TYPES.TRANSPORT_ORDER, id: "LIST" },
      ],
    }),
    deleteTransportOrder: builder.mutation<
      { statusCode: number; success: boolean; data: TransportOrder },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${URL_TRANSPORT_ORDER}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: TAG_TYPES.TRANSPORT_ORDER, id },
        { type: TAG_TYPES.TRANSPORT_ORDER, id: "LIST" },
      ],
    })
  }),
});

export const {
  useGetTransportOrdersQuery,
  useCreateTransportOrderMutation,
  useGetTransportOrderDetailQuery,
  useUpdateTransportOrderMutation,
  useUpdateTransportOrderContainerMutation,
  useCreateTransportOrderContainerMutation,
  useRemoveTransportOrderContainerMutation,
  useDeleteTransportOrderMutation,
} = transportApiService;
